import * as T from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SimplifyModifier } from 'three/examples/jsm/modifiers/SimplifyModifier.js';
import type { ImpostorSpec } from './impostor';

/** Game-ready environment assets produced by art/blender (see public/assets/env/manifest.json). */
/** `lod` is a simplified copy (about a quarter of the triangles) for distant or numerous instances. */
export interface MeshAsset { id: string; geometry: T.BufferGeometry; lod: T.BufferGeometry; material: T.Material; size: T.Vector3 }
export interface EnvLibrary { meshes: Map<string, MeshAsset>; impostors: Map<string, ImpostorSpec> }

const BASE = `${import.meta.env.BASE_URL}assets/env/`;
const MESHES = ['boulder_01', 'coast_rocks_05', 'dead_tree_trunk', 'namaqualand_boulder_02', 'rock_moss_set_01_a', 'rock_moss_set_01_b', 'fern_02', 'shrub_02', 'wild_rooibos_bush', 'dandelion_01', 'flower_gazania'];

interface ManifestEntry { id: string; kind: string; files?: string[] | Record<string, string>; views?: number; cell?: [number, number]; size?: [number, number] | Record<string, number> }
function entries(manifest: unknown): ManifestEntry[] {
  const raw = manifest && typeof manifest === 'object' ? (Array.isArray(manifest) ? manifest : (manifest as { assets?: unknown }).assets ?? Object.values(manifest)) : [];
  return (Array.isArray(raw) ? raw : []).filter((e): e is ManifestEntry => !!e && typeof e === 'object' && typeof (e as ManifestEntry).id === 'string');
}
function fileList(e: ManifestEntry) { return Array.isArray(e.files) ? e.files : Object.values(e.files ?? {}); }

let library: Promise<EnvLibrary> | null = null; let ready: EnvLibrary | null = null;
export function envLibraryNow() { return ready; }
/** True while the library is downloading: callers skip stand-ins to avoid a visible swap. */
export function envLibraryLoading() { return !!library && !ready; }
export function loadEnvLibrary(anisotropy: number): Promise<EnvLibrary> {
  library ??= (async () => {
    const lib: EnvLibrary = { meshes: new Map(), impostors: new Map() };
    let list: ManifestEntry[] = [];
    try { const r = await fetch(`${BASE}manifest.json`); if (r.ok) list = entries(await r.json()); } catch { /* offline or missing */ }
    const gltf = new GLTFLoader();
    const meshIds = new Set([...MESHES, ...list.filter(e => e.kind === 'mesh').map(e => e.id)]);
    await Promise.all([...meshIds].map(async id => {
      const entry = list.find(e => e.id === id), file = entry ? fileList(entry).find(f => f.endsWith('.glb')) : undefined;
      const url = file ? `${BASE}${file.replace(/^.*assets\/env\//, '')}` : `${BASE}meshes/${id}.glb`;
      try {
        const scene = (await gltf.loadAsync(url)).scene; let found: T.Mesh | null = null;
        scene.updateMatrixWorld(true); scene.traverse(o => { if (!found && o instanceof T.Mesh) found = o; });
        if (!found) return;
        const mesh = found as T.Mesh, geometry = mesh.geometry.clone().applyMatrix4(mesh.matrixWorld); geometry.computeBoundingBox();
        const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
        for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap'] as const) { const t = (material as T.MeshStandardMaterial)[key]; if (t) t.anisotropy = anisotropy; }
        let lod = geometry;
        if (/rock|boulder|trunk/.test(id) && geometry.attributes.position.count > 500) {
          try { lod = new SimplifyModifier().modify(geometry.clone(), Math.floor(geometry.attributes.position.count * .72)); lod.computeVertexNormals(); lod.computeBoundingBox(); } catch { lod = geometry; }
        }
        lib.meshes.set(id, { id, geometry, lod, material, size: geometry.boundingBox!.getSize(new T.Vector3()) });
      } catch { /* asset not built yet: procedural stand-ins stay */ }
    }));
    const textures = new T.TextureLoader();
    await Promise.all(list.filter(e => e.kind === 'impostor').map(async e => {
      const files = fileList(e), albedo = files.find(f => /albedo/.test(f)), normal = files.find(f => /normal/.test(f));
      const size = Array.isArray(e.size) ? e.size : e.size ? [e.size.width ?? e.size.widthM ?? e.size.w, e.size.height ?? e.size.heightM ?? e.size.h] : null;
      if (!albedo || !normal || !size || !size[0] || !size[1]) return;
      const load = async (f: string, color: boolean) => {
        const t = await textures.loadAsync(`${BASE}${f.replace(/^.*assets\/env\//, '')}`);
        t.colorSpace = color ? T.SRGBColorSpace : T.NoColorSpace; t.anisotropy = Math.min(4, anisotropy); t.generateMipmaps = true; t.minFilter = T.LinearMipmapLinearFilter;
        return t;
      };
      try {
        const [a, n] = await Promise.all([load(albedo, true), load(normal, false)]);
        lib.impostors.set(e.id, { id: e.id, albedo: a, normal: n, views: e.views ?? 8, width: size[0] as number, height: size[1] as number });
      } catch { /* missing atlas */ }
    }));
    ready = lib; return lib;
  })();
  return library;
}
